import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AppService } from '../../../services';
import { NgxSkeletonLoaderConfig } from 'ngx-skeleton-loader';
import { MapService } from 'src/app/shared/services/map.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import { CommonService } from '../../../services/common.service';
import { GetVideoIdRequest, UploadFileRequest } from '../../../core/shared.actions';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MarkupService } from '../../../services/markup.service';
import { ProjectsService } from '../../../../modules/projects/core/projects.service';
import { classifyFile } from '../../../helpers';

@Component({
    selector: 'app-pictures-section',
    templateUrl: './pictures-section.component.html',
    styleUrls: ['./pictures-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PicturesSectionComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInputRef: any;
  @Input() pictures: any = [];
  @Input() isEditable = true;
  @Input() add = false;
  /** When set, the add (+) block is hidden once pictures count reaches this limit. When not set, add block is always shown when add is true. */
  @Input() maxPictures?: number;
  @Input() maxHeight?: string;
  @Input() label = '';
  @Input() selectedLineItem: any;
  @Input() openSideNavPic = false;
  @Output() onDelete: EventEmitter<any> = new EventEmitter();
  loading = true;

  loaderAppearance: NgxSkeletonLoaderConfig['appearance'] = '';
  loaderTheme: NgxSkeletonLoaderConfig['theme'] = {
    width: '100%',
    height: '100%',
  };
  dummyUrl = 'assets/images/png/video-processing.png';
  limits: any;
  markupPreviews: { [id: string]: string } = {};
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public appService: AppService,
    public mapService: MapService,
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private markupService: MarkupService,
    private cdr: ChangeDetectorRef,
    private projectsService: ProjectsService,
  ) {
  }

  /** Use full-screen overlay only when explicitly requested via input */
  get useFullScreenOverlay(): boolean {
    return this.openSideNavPic;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedLineItem']) {
      this.selectedLineItem = changes['selectedLineItem'];
    }
  }

  ngOnInit() {
    this.markupService.markupSaved$.pipe(takeUntil(this.onDestroy)).subscribe(({ attachmentId, previewDataUrl }) => {
      if (attachmentId && previewDataUrl) {
        this.markupPreviews[attachmentId] = previewDataUrl;
        // Markup is saved via its own API — don't mark parent form dirty
      }
      setTimeout(() => this.cdr.detectChanges());
    });
  }

  getThumbUrl(picture: any): string {
    return this.markupPreviews[picture?.id] || this.markupService.getMarkupPreview(picture?.id) || picture?.thumb_url;
  }

  hideLoader() {
    this.loading = false;
  }

  handleError() {
    this.hideLoader();
  }

  deletePicture(picture) {
    this.onDelete.emit(picture);
  }

  openSideNavPictures(picture) {

    if (!picture || !picture.id) {
      console.error('Invalid picture data');
      return;
    }

    // If selectedLineItem is available, use its pictures array
    if (this.selectedLineItem) {
      const imageData = structuredClone(this.selectedLineItem.currentValue);

      if (!imageData || !Array.isArray(imageData.pictures) || imageData.pictures.length === 0) {
        console.error('Invalid or empty pictures array in imageData');
        return;
      }

      // Efficiently regroup the pictures array based on picture.id
      const selectedPictures = imageData.pictures.filter((img: any) => img.id === picture.id);
      const remainingPictures = imageData.pictures.filter((img: any) => img.id !== picture.id);

      // Ensure selected picture exists before modifying the array
      if (selectedPictures.length > 0) {
        imageData.pictures = selectedPictures.concat(remainingPictures);
      }

      console.log('📸 Sending to selectedPicturesData:', imageData.pictures);
      console.log('🎯 Pictures array length:', imageData.pictures.length);

      // Trigger picture overlay (PictureOverlayComponent with Swiper)
      this.mapService.selectedPicturesData.next(imageData.pictures);
    } else if (this.pictures && this.pictures.length > 0) {
      // Fallback: use the pictures array passed as input
      // Reorder so the clicked picture is first
      const selectedPictures = this.pictures.filter((img: any) => img.id === picture.id);
      const remainingPictures = this.pictures.filter((img: any) => img.id !== picture.id);
      const reorderedPictures = selectedPictures.concat(remainingPictures);

      console.log('📸 Using input pictures array:', reorderedPictures);
      console.log('🎯 Pictures array length:', reorderedPictures.length);

      // Trigger picture overlay
      this.mapService.selectedPicturesData.next(reorderedPictures);
    } else {
      console.error('No pictures available to display');
    }
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files; // Get selected files
    const imageFormData = new FormData();
    const videoFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (classifyFile(file) === 'video') {
        videoFiles.push(file);
      } else {
        imageFormData.append('inputFiles', file);
      }
    }

    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }

    if (videoFiles.length > 0) {
      videoFiles.forEach((videoFile: File) => {
        const videoElement = document.createElement('video');
        const reader = new FileReader();

        reader.onloadend = () => {
          videoElement.src = reader.result as string;

          videoElement.onloadedmetadata = () => {
            const videoDuration = videoElement.duration;
            const maxDuration = this.limits.video_size_in_secs;

            if (videoDuration > maxDuration) {
              this.commonService.notification(
                `Video duration is ${videoDuration} seconds, should be less than ${maxDuration} seconds.`,
                'success',
              );
            } else {
              this.store.dispatch(GetVideoIdRequest({ payload: videoFile }));
            }
          };
        };

        reader.readAsDataURL(videoFile);
      });
    }

    this.fileInputRef.nativeElement.value = '';
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
